import React, { useEffect, useState } from 'react';
import { VbzDropzone, VbzModal } from '@smiles/voebiz-ui-kit';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { deleteBeneficiariesBatch } from '../../services/services';
import LoadedFile from '../LoadedFile';
import DeactiveInstructions from '../DeactiveInstructions';
import DeactivationErrorModal from '../ModalDeactivationError';
import SuccessDeactivationTeam from '../ModalSuccessDeactivation';
import ConfirmDeactivationTeam from '../ModalConfirmDIsableTeam';
import IncludeModalButtons from '../IncludeModalButtons';

interface ModalBeneficiariesDeactivationPros {
  isOpen: boolean;
  closeModal: () => void;
}

const ModalBeneficiariesDeactivation = ({ isOpen, closeModal }: ModalBeneficiariesDeactivationPros) => {
  const { t } = useTranslation('modalBeneficiariesDeactivation');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File[]>([]);
  const [isInstructionsActive, setIsInstructionsActive] = useState(false);
  const [confirmDeactivationModal, setConfirmDeactivationModal] = useState(false);
  const [successDeactivationModal, setSuccessDeactivationModal] = useState(false);
  const [errorSendFile, setErrorSendFile] = useState(false);
  const [filename, setFilename] = useState('');

  const hiddenBackButton = successDeactivationModal || errorSendFile ? !isMobile : true;
  const showCloseFromMobileSuccess = (isMobile && successDeactivationModal) || (isMobile && errorSendFile);

  const handleSendFile = () => {
    setConfirmDeactivationModal(true);
  };

  const handleCloseModalBeneficiaries = () => {
    if (!isInstructionsActive) {
      closeModal();
      setSelectedFile([]);
      setConfirmDeactivationModal(false);
      setSuccessDeactivationModal(false);
      setErrorSendFile(false);
    }
    setIsInstructionsActive(false);
  };

  const handleShowInstructions = () => setIsInstructionsActive(true);
  const handleClearFile = () => setSelectedFile([]);

  const handleConfirmDeactivation = () => {
    setIsLoading(true);
    const reader = new FileReader();
    reader.readAsDataURL(selectedFile[0]);
    reader.onload = async () => {
      const data = await deleteBeneficiariesBatch(reader.result as string);
      if (data.statusCode !== 200 && !data.success) {
        setErrorSendFile(true);
        setConfirmDeactivationModal(false);
        setIsLoading(false);
      }
      setFilename(data.data?.filename);
      setSuccessDeactivationModal(data.success);
      setConfirmDeactivationModal(false);
      setIsLoading(false);
    };
  };

  const handleDeactivateAnotherTeam = () => {
    setSuccessDeactivationModal(false);
    setSelectedFile([]);
  };

  const handleTryAgain = () => {
    setSelectedFile([]);
    setErrorSendFile(false);
  };

  useEffect(() => {
    setSelectedFile([]);
    setIsInstructionsActive(false);
    setConfirmDeactivationModal(false);
    setSuccessDeactivationModal(false);
  }, []);

  return (
    <VbzModal
      isOpen={isOpen}
      type="right"
      toggle={handleCloseModalBeneficiaries}
      closedButton={isInstructionsActive || showCloseFromMobileSuccess}
      backButton={!isInstructionsActive && hiddenBackButton}
      id="modal-disable-teams"
      className="include-beneficiary-modal"
    >
      <div className="include-modal-container">
        {!isInstructionsActive && !successDeactivationModal && !confirmDeactivationModal && !errorSendFile && (
          <>
            <span className="title deactivate">{t('title')}</span>
            <div data-testid="test-uploader">
              <IncludeModalButtons
                handleShowInstructions={handleShowInstructions}
                downloadArchive="modeloDeExclusao"
                title={t('need_help')}
              />
              {selectedFile.length === 0 ? (
                <VbzDropzone
                  extensions={['text/csv']}
                  textExtensions={t('csv_file')}
                  textDesktop={t('select_files')}
                  textMobile={t('select_files_mobile')}
                  cbOnFilesAdded={(file) => setSelectedFile(file)}
                />
              ) : (
                <LoadedFile isDeactivate selectedFile={selectedFile} handleClearFile={handleClearFile} />
              )}
            </div>

            <div className="button-container">
              <span
                style={{ position: 'relative', cursor: 'pointer', marginBottom: 3 }}
                onClick={handleCloseModalBeneficiaries}
              >
                {t('go_back')}
                <hr
                  style={{
                    width: '42px',
                    border: '1px solid #999999',
                    borderRadius: '100px',
                    position: 'absolute',
                    margin: 0,
                  }}
                />
              </span>
              <button
                onClick={handleSendFile}
                disabled={selectedFile.length < 1}
                className="include-modal-container-disabled-team"
                data-testid="test-include-button"
                type="button"
              >
                {t('send_file')}
              </button>
            </div>
          </>
        )}
        {isInstructionsActive && (
          <div className="deactivate-instructions">
            <DeactiveInstructions />
            <button
              type="button"
              className="include-modal-container-go-back"
              onClick={() => setIsInstructionsActive(false)}
            >
              {t('go_back')}
            </button>
          </div>
        )}

        {confirmDeactivationModal && (
          <div>
            <ConfirmDeactivationTeam handleConfirm={handleConfirmDeactivation} isLoading={isLoading} />
          </div>
        )}

        {successDeactivationModal && (
          <div>
            <SuccessDeactivationTeam toggle={handleDeactivateAnotherTeam} filename={filename} />
          </div>
        )}

        {errorSendFile && (
          <div>
            <DeactivationErrorModal toggleErrorModal={handleTryAgain} />
          </div>
        )}
      </div>
    </VbzModal>
  );
};

export default ModalBeneficiariesDeactivation;
